import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    OnDestroy,
    viewChildren,
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {InputDateGroupComponent} from '@brunata-angular/ui';
import {DateTime} from 'luxon';
import {MeterService} from '../../meter.service';
import {CommonModule} from '@angular/common';
import {
    CheckboxGroupComponent,
    DatePipe,
    InputNumberGroupComponent,
    LoadComponent,
    NumberPipe,
    PaginationComponent,
} from '@brunata-angular/ui';
import {
    AppValidators,
    BuildingServices,
    caseInsensitiveComparator,
    comparator,
    ExposedValueWhileLoading,
    FeatureFlagService,
    filterNulls,
    fromControl,
    fromControlWithDebounceTime,
    keyComparator,
    numericLocaleComparator,
    proBraLocComparator,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {applySortingToHeaders, SortableTableHeaderComponent, Sorting} from '@brunata-angular/ui';
import {
    BehaviorSubject,
    combineLatest,
    debounceTime,
    filter,
    map,
    Observable,
    scan,
    startWith,
    switchMap,
    tap,
} from 'rxjs';
import {NgbDropdown, NgbDropdownMenu, NgbDropdownToggle} from '@ng-bootstrap/ng-bootstrap';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {InputDropdownCheckboxesComponent, SelectableItem} from '@brunata-angular/ui';
import {MeasuringPoint} from './measuring-point.model';
import {selectBuilding} from 'src/app/state/selectors';
import {Store} from '@ngrx/store';
import {BuildingDetails} from '@shared/models/building-details';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';

export type MeasuringPointFilter = {
    proBraLoc: string | null;
    name: string | null;
    address: string | null;
    serialNo: string | null;
    connectedTo: string | null;
    roomId: string | null;
    alloUnitType: SelectableItem[];
    meterSuperTypeNo: SelectableItem[];
    mountingDate: DateTime | null;
    dismountedDate: DateTime | null;
    unitFactor: string | null;
    scale: string | null;
    readingDate: DateTime | null;
    neverReceivedReading: boolean;
    noReadingForDays: number | null;
    meterValue: string | null;
    unit: SelectableItem[];
};

type DateForm = {
    selectedDate: FormControl<DateTime | null>;
};

type MeasuringPointsView = {
    buildingNo: string;
    hasRemoteReadingDataService: boolean;
    isNoReadingFilterEnabled: boolean;
    measuringPoints: ExposedValueWhileLoading<MeasuringPoint[]>;
    pageSize: number;
    selectedDate: DateTime;
};

@Component({
    selector: 'app-measuring-points-table',
    standalone: true,
    imports: [
        CommonModule,
        TranslatePipe,
        LoadComponent,
        ReactiveFormsModule,
        SortableTableHeaderComponent,
        DatePipe,
        PaginationComponent,
        InputTextGroupComponent,
        InputDropdownCheckboxesComponent,
        InputDateGroupComponent,
        InputNumberGroupComponent,
        CheckboxGroupComponent,
        NgbDropdown,
        NgbDropdownMenu,
        NgbDropdownToggle,
        NumberPipe,
    ],
    templateUrl: './measuring-points-table.component.html',
    styleUrl: './measuring-points-table.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasuringPointsTableComponent implements OnDestroy {
    private readonly headers = viewChildren(SortableTableHeaderComponent);
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly meterService = inject(MeterService);
    private readonly sorting = toSignal(this.meterService.sorting, {requireSync: true});
    private readonly translateService = inject(TranslateService);
    private readonly featureFlagService = inject(FeatureFlagService);
    page: BehaviorSubject<number> = new BehaviorSubject<number>(1);

    selectedColumnsForm = this.formBuilder.group({
        proBraLoc: [true],
        consumerName: [true],
        address: [true],
        serialNo: [true],
        connectedTo: [false],
        roomId: [true],
        alloUnitType: [true],
        meterSuperTypeNo: [true],
        mountingDate: [false],
        dismountedDate: [false],
        unitFactor: [false],
        scale: [false],
        readingDate: [true],
        meterValue: [true],
        unit: [true],
    });

    formGroup: FormGroup<DateForm> = this.formBuilder.group({
        selectedDate: new FormControl<DateTime>(DateTime.now(), [
            Validators.required,
            AppValidators.maxDate(DateTime.now()),
        ]),
    });

    formGroupFilter = this.formBuilder.group({
        proBraLoc: new FormControl<string | null>(''),
        name: new FormControl<string | null>(''),
        address: new FormControl<string | null>(''),
        serialNo: new FormControl<string | null>(''),
        connectedTo: new FormControl<string | null>(''),
        roomId: new FormControl<string | null>(''),
        alloUnitType: new FormControl<SelectableItem[] | null>([]),
        meterSuperTypeNo: new FormControl<SelectableItem[] | null>([]),
        mountingDate: new FormControl<DateTime | null>(null),
        dismountedDate: new FormControl<DateTime | null>(null),
        unitFactor: new FormControl<string | null>(''),
        scale: new FormControl<string | null>(''),
        readingDate: new FormControl<DateTime | null>(null),
        neverReceivedReading: new FormControl<boolean>(false, {nonNullable: true}),
        noReadingForDays: new FormControl<number | null>(null, [
            AppValidators.integer,
            Validators.min(1),
        ]),
        meterValue: new FormControl<string>(''),
        unit: new FormControl<SelectableItem[] | null>([]),
    });

    private readonly buildingDetails$: Observable<BuildingDetails> = this.store
        .select(selectBuilding)
        .pipe(
            map(buildingState =>
                buildingState.status === 'success' ? buildingState.buildingDetails : null,
            ),
            filterNulls(),
        );

    private readonly selectedDate$: Observable<DateTime> = fromControlWithDebounceTime(
        this.formGroup.controls.selectedDate,
    ).pipe(filterNulls());

    private readonly isNoReadingFilterEnabled$ = this.featureFlagService.isFeatureEnabled$(
        'agi-20300-no-reading-filter',
    );

    private readonly hasRemoteReadingDataService$: Observable<boolean> = this.buildingDetails$.pipe(
        map(buildingDetails =>
            buildingDetails.buildingServices.includes(BuildingServices.REMOTE_READING_DATA),
        ),
    );

    // Null while the controls are off screen, so a hidden no-reading filter stops filtering.
    private readonly noReadingReferenceDate$: Observable<DateTime | null> = combineLatest([
        this.isNoReadingFilterEnabled$,
        this.hasRemoteReadingDataService$,
        fromControl(this.selectedColumnsForm.controls.readingDate),
        this.selectedDate$,
    ]).pipe(
        map(([isEnabled, hasRemoteReadingDataService, isReadingDateShown, selectedDate]) =>
            isEnabled && hasRemoteReadingDataService && isReadingDateShown === true
                ? selectedDate
                : null,
        ),
    );

    private readonly measuringPoints$: Observable<ExposedValueWhileLoading<MeasuringPoint[]>> =
        combineLatest([
            this.buildingDetails$.pipe(map(buildingDetails => buildingDetails.building.buildingNo)),
            this.selectedDate$,
        ]).pipe(
            switchMap(([buildingNo, selectedDate]) =>
                this.meterService.getMeasuringPoints(buildingNo, selectedDate).pipe(
                    map(measuringPoints => ({
                        loading: false,
                        value: measuringPoints,
                    })),
                    startWith({loading: true, value: []}),
                ),
            ),
            scan((prev, current) => ({
                loading: current.loading,
                value: current.loading ? prev.value : current.value,
            })),
            tap(measuringPoints => {
                if (measuringPoints.loading) {
                    return;
                }
                if (this.meterService.measuringPointFilterSubject.value == null) {
                    this.formGroupFilter.patchValue({
                        meterSuperTypeNo: this.getUniqueValuesForLists(
                            measuringPoints.value,
                            'meterSuperTypeNo',
                        ),
                        alloUnitType: this.getUniqueValuesForLists(
                            measuringPoints.value,
                            'alloUnitType',
                        ),
                        unit: this.getUniqueValuesForLists(measuringPoints.value, 'unit'),
                    });
                } else {
                    this.formGroupFilter.patchValue(
                        this.meterService.measuringPointFilterSubject.value,
                    );
                }
            }),
        );

    // Not fromControlWithDebounceTime: its group-validity gate would freeze every column filter
    // while the days box holds an invalid value.
    private readonly filterValues$: Observable<MeasuringPointFilter> =
        this.formGroupFilter.valueChanges.pipe(
            debounceTime(200),
            startWith(this.formGroupFilter.value),
            tap(() => this.page.next(1)),
            map(() => {
                const formGroupValues = this.formGroupFilter.getRawValue();
                return {
                    ...formGroupValues,
                    proBraLoc: formGroupValues.proBraLoc ?? '',
                    name: formGroupValues.name ?? '',
                    address: formGroupValues.address ?? '',
                    serialNo: formGroupValues.serialNo ?? '',
                    connectedTo: formGroupValues.connectedTo ?? '',
                    roomId: formGroupValues.roomId ?? '',
                    alloUnitType: formGroupValues.alloUnitType ?? [],
                    meterSuperTypeNo: formGroupValues.meterSuperTypeNo ?? [],
                    unitFactor: formGroupValues.unitFactor ?? '',
                    scale: formGroupValues.scale ?? '',
                    noReadingForDays: this.validNoReadingForDays(),
                    meterValue: formGroupValues.meterValue ?? '',
                    unit: formGroupValues.unit ?? [],
                };
            }),
        );

    private readonly measuringPointsData$: Observable<ExposedValueWhileLoading<MeasuringPoint[]>> =
        combineLatest([
            this.measuringPoints$,
            this.filterValues$,
            this.noReadingReferenceDate$,
            this.meterService.sorting,
        ]).pipe(
            map(([measuringPoints, filterValues, noReadingReferenceDate, sorting]) => {
                const filteredMeasuringPoints = this.filterMeasuringPoints(
                    measuringPoints.value,
                    filterValues,
                    noReadingReferenceDate,
                );
                const sortedMeasuringPoints = this.sortMeasuringPoints(
                    filteredMeasuringPoints,
                    sorting,
                );

                return {
                    loading: measuringPoints.loading,
                    value: sortedMeasuringPoints,
                };
            }),
        );

    readonly view$: Observable<MeasuringPointsView> = combineLatest([
        this.measuringPointsData$,
        this.buildingDetails$.pipe(map(buildingDetails => buildingDetails.building.buildingNo)),
        this.hasRemoteReadingDataService$,
        this.isNoReadingFilterEnabled$,
        toObservable(this.meterService.pageSize),
        this.selectedDate$,
    ]).pipe(
        filter(([measuringPoints]) => !measuringPoints.loading || measuringPoints.value.length > 0),
        map(
            ([
                measuringPoints,
                buildingNo,
                hasRemoteReadingDataService,
                isNoReadingFilterEnabled,
                pageSize,
                selectedDate,
            ]) => ({
                buildingNo,
                measuringPoints,
                pageSize: pageSize,
                hasRemoteReadingDataService,
                isNoReadingFilterEnabled,
                selectedDate,
            }),
        ),
    );

    constructor() {
        effect(() => {
            applySortingToHeaders(this.headers(), this.sorting());
        });
    }

    ngOnDestroy(): void {
        this.saveFilter();
    }

    setPage(page: number): void {
        this.page.next(page);
    }

    onSort(sorting: Sorting): void {
        this.meterService.sorting.next(sorting);
    }

    changeDateByDay(days: number): void {
        const selectedDate = this.formGroup.value.selectedDate;
        const updatedDate = selectedDate && selectedDate.plus({days});
        if (updatedDate && updatedDate <= DateTime.now()) {
            this.formGroup.patchValue({
                selectedDate: updatedDate,
            });
        }
    }

    filterDate(
        measuringPoint: MeasuringPoint,
        date: DateTime | null,
        key: keyof MeasuringPoint,
    ): boolean {
        if (date == null || !date.isValid) {
            return true;
        }
        const dateToCheck = measuringPoint[key];
        return dateToCheck instanceof DateTime && dateToCheck.hasSame(date, 'day');
    }

    filterNoReading(
        measuringPoint: MeasuringPoint,
        filterValue: MeasuringPointFilter,
        referenceDate: DateTime | null,
    ): boolean {
        const noReadingForDays = filterValue.noReadingForDays;
        if (
            referenceDate == null ||
            (!filterValue.neverReceivedReading && noReadingForDays == null)
        ) {
            return true;
        }
        // A manually read meter has no telegrams to miss, as in the Meter Alarms report it replaces
        if (measuringPoint.transmitting === false) {
            return false;
        }
        if (measuringPoint.readingDate == null) {
            return filterValue.neverReceivedReading;
        }
        return (
            noReadingForDays != null &&
            measuringPoint.readingDate <
                referenceDate.startOf('day').minus({days: noReadingForDays})
        );
    }

    filteredArrayValues(value: string | null, arrayFilterValue: SelectableItem[]): boolean {
        const valuesToFilter = arrayFilterValue.filter(it => it.selected).map(it => it.name);

        if (valuesToFilter.length === 0) {
            return true;
        }
        return arrayFilterValue.some(it => it.selected && it.name === value);
    }

    filterMeasuringPoints(
        measuringPoints: MeasuringPoint[],
        filterValue: MeasuringPointFilter,
        noReadingReferenceDate: DateTime | null,
    ): MeasuringPoint[] {
        return measuringPoints.filter(
            measuringPoint =>
                ((measuringPoint.propertyNo == null &&
                    measuringPoint.branchNo == null &&
                    measuringPoint.locationNo == null &&
                    (filterValue.proBraLoc ?? '') === '') ||
                    (
                        (measuringPoint.propertyNo ?? '') +
                        '-' +
                        (measuringPoint.branchNo ?? '') +
                        '-' +
                        (measuringPoint.locationNo ?? '')
                    )
                        .toLowerCase()
                        .includes((filterValue.proBraLoc ?? '').toLowerCase())) &&
                ((filterValue.address ?? '') === '' ||
                    (measuringPoint.address != null &&
                        measuringPoint.address
                            .toLowerCase()
                            .includes((filterValue.address ?? '').toLowerCase()))) &&
                ((filterValue.name ?? '') === '' ||
                    (measuringPoint.consumerName != null &&
                        measuringPoint.consumerName
                            .toLowerCase()
                            .includes((filterValue.name ?? '').toLowerCase()))) &&
                ((filterValue.serialNo ?? '') === '' ||
                    (measuringPoint.serialNo != null &&
                        measuringPoint.serialNo
                            .toLowerCase()
                            .includes((filterValue.serialNo ?? '').toLowerCase()))) &&
                ((filterValue.connectedTo ?? '') === '' ||
                    (measuringPoint.connectedTo != null &&
                        measuringPoint.connectedTo
                            .toLowerCase()
                            .includes((filterValue.connectedTo ?? '').toLowerCase()))) &&
                this.filteredArrayValues(
                    this.translateService.translate(
                        'mappers_allocationUnitMap.' + measuringPoint.alloUnitType,
                        'online',
                        'common',
                    ),
                    filterValue.alloUnitType,
                ) &&
                this.filteredArrayValues(
                    this.translateService.translate(
                        'mappers_measurementUnit.' + measuringPoint.unit,
                        'online',
                        'common',
                    ),
                    filterValue.unit,
                ) &&
                this.filteredArrayValues(
                    this.translateService.translate(
                        'mappers_meterType.' + measuringPoint.meterSuperTypeNo,
                        'online',
                        'common',
                    ),
                    filterValue.meterSuperTypeNo,
                ) &&
                ((filterValue.serialNo ?? '') === '' ||
                    (measuringPoint.serialNo != null &&
                        measuringPoint.serialNo.includes(filterValue.serialNo ?? ''))) &&
                ((filterValue.scale ?? '') === '' ||
                    (measuringPoint.scale != null &&
                        measuringPoint.scale.toString().includes(filterValue.scale ?? ''))) &&
                ((filterValue.roomId ?? '') === '' ||
                    (measuringPoint.roomId != null &&
                        measuringPoint.roomId
                            .toLowerCase()
                            .includes((filterValue.roomId ?? '').toLowerCase()))) &&
                ((filterValue.unitFactor ?? '') === '' ||
                    (measuringPoint.unitFactor != null &&
                        measuringPoint.unitFactor
                            .toString()
                            .includes(filterValue.unitFactor ?? ''))) &&
                this.filterDate(measuringPoint, filterValue.mountingDate, 'mountingDate') &&
                this.filterDate(measuringPoint, filterValue.dismountedDate, 'dismountedDate') &&
                this.filterDate(measuringPoint, filterValue.readingDate, 'readingDate') &&
                this.filterNoReading(measuringPoint, filterValue, noReadingReferenceDate) &&
                ((filterValue.meterValue ?? '') === '' ||
                    (measuringPoint.meterValue != null &&
                        measuringPoint.meterValue
                            .toString()
                            .includes((filterValue.meterValue ?? '').toLowerCase()))),
        );
    }

    downloadFile(view: MeasuringPointsView): void {
        const currentDate = DateTime.now();
        const onDate = this.formGroup.controls.selectedDate.valid
            ? (this.formGroup.value.selectedDate ?? currentDate)
            : currentDate;
        if (view.isNoReadingFilterEnabled) {
            this.meterService.downloadSelectedMeasuringPointsExportFile(
                view.buildingNo,
                onDate,
                view.measuringPoints.value.flatMap(measuringPoint =>
                    measuringPoint.meterId == null
                        ? []
                        : [
                              {
                                  meterId: measuringPoint.meterId,
                                  alloUnitType: measuringPoint.alloUnitType,
                              },
                          ],
                ),
            );
            return;
        }
        this.meterService.downloadMeasuringPointsExportFile(view.buildingNo, onDate);
    }

    resetColumns(): void {
        this.selectedColumnsForm.patchValue({
            proBraLoc: true,
            consumerName: true,
            address: true,
            serialNo: true,
            connectedTo: false,
            roomId: true,
            alloUnitType: true,
            meterSuperTypeNo: true,
            mountingDate: false,
            dismountedDate: false,
            unitFactor: false,
            scale: false,
            readingDate: true,
            meterValue: true,
            unit: true,
        });
    }

    private sortMeasuringPoints(
        measuringPoints: MeasuringPoint[],
        sorting: Sorting,
    ): MeasuringPoint[] {
        if (sorting.column == null || measuringPoints.length === 0) {
            return measuringPoints;
        }

        const columnComparator = this.getColumnComparator(
            sorting.column as keyof MeasuringPoint,
            sorting.direction ?? 'asc',
        );

        return [...measuringPoints].sort(columnComparator);
    }

    private getColumnComparator(
        column: keyof MeasuringPoint,
        order: 'asc' | 'desc' = 'asc',
    ): (a: MeasuringPoint, b: MeasuringPoint) => number {
        const selector = (measuringPoint: MeasuringPoint): string =>
            measuringPoint[column]?.toString().trim() ?? '';

        switch (column) {
            case 'proBraLoc':
                return proBraLocComparator(order);
            case 'meterSequenceNo':
            case 'serialNo':
            case 'connectedTo':
            case 'meterValue':
                return numericLocaleComparator<MeasuringPoint>(selector, order);
            case 'consumerName':
            case 'address':
            case 'roomId':
                return caseInsensitiveComparator(selector, order);
            case 'alloUnitType':
                return comparator({
                    selector: (measuringPoint: MeasuringPoint) =>
                        this.translateService.translate(
                            'mappers_allocationUnitMap.' + measuringPoint.alloUnitType,
                            'online',
                            'common',
                        ),
                    order: order,
                });
            case 'meterSuperTypeNo':
                return comparator({
                    selector: (measuringPoint: MeasuringPoint) =>
                        this.translateService.translate(
                            'mappers_meterType.' + measuringPoint.meterSuperTypeNo,
                            'online',
                            'common',
                        ),
                    order: order,
                });
            default:
                return keyComparator(selector, order);
        }
    }

    private getUniqueValuesForLists(
        measuringPoints: MeasuringPoint[],
        key: keyof MeasuringPoint,
    ): SelectableItem[] {
        const uniqueNames = new Set<string>();
        const selectableItems: SelectableItem[] = [];

        let translateKeyPrefix = '';
        if (key === 'alloUnitType') {
            translateKeyPrefix = 'mappers_allocationUnitMap.';
        } else if (key === 'unit') {
            translateKeyPrefix = 'mappers_measurementUnit.';
        } else if (key === 'meterSuperTypeNo') {
            translateKeyPrefix = 'mappers_meterType.';
        }

        measuringPoints.forEach(measuringPoint => {
            const value = measuringPoint[key];
            if (value != null) {
                const valueStr = value instanceof DateTime ? value.toISO() : value.toString();
                const translateKey = translateKeyPrefix + valueStr;

                const name = this.translateService.translate(translateKey, 'online', 'common');
                if (!uniqueNames.has(name)) {
                    uniqueNames.add(name);
                    selectableItems.push({
                        name: name,
                        selected: false,
                    });
                }
            }
        });

        return selectableItems.sort((a, b) => a.name.localeCompare(b.name));
    }

    private validNoReadingForDays(): number | null {
        const control = this.formGroupFilter.controls.noReadingForDays;
        return control.valid ? control.value : null;
    }

    private saveFilter(): void {
        const rawValues = this.formGroupFilter.getRawValue();
        const valuesToSave: MeasuringPointFilter = {
            proBraLoc: rawValues.proBraLoc,
            name: rawValues.name,
            address: rawValues.address,
            serialNo: rawValues.serialNo,
            connectedTo: rawValues.connectedTo,
            roomId: rawValues.roomId,
            alloUnitType:
                rawValues.alloUnitType?.map(it => ({name: it.name, selected: it.selected})) ?? [],
            meterSuperTypeNo:
                rawValues.meterSuperTypeNo?.map(it => ({name: it.name, selected: it.selected})) ??
                [],
            mountingDate: rawValues.mountingDate,
            dismountedDate: rawValues.dismountedDate,
            unitFactor: rawValues.unitFactor,
            scale: rawValues.scale,
            readingDate: rawValues.readingDate,
            neverReceivedReading: rawValues.neverReceivedReading,
            noReadingForDays: this.validNoReadingForDays(),
            meterValue: rawValues.meterValue,
            unit: rawValues.unit?.map(it => ({name: it.name, selected: it.selected})) ?? [],
        };
        this.meterService.setMeasuringFilterColumns(valuesToSave);
    }

    setPageSize(pageSize: number): void {
        this.meterService.pageSize.set(pageSize);
    }
}
